import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Plus, Trash2, AlertCircle, Check, InfinityIcon } from 'lucide-react';
import {
  getPlanById,
  putPlan,
  getFeatures,
  createFeature,
  deleteFeature,
  type CreatePlanPayload
} from '../services/subscriptionService';

interface FeatureI {
  featureId: number;
  included: boolean;
}

interface EditPlanFormData {
  name: string;
  description: string;
  priceEuro: string;
  icon: string;
  popular: boolean;
  limit: string;
  active: boolean;
}

const EditPlan = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { planId } = useParams<{ planId: string }>();
  
  const [formData, setFormData] = useState<EditPlanFormData>({
    name: '',
    description: '',
    priceEuro: '',
    icon: '',
    popular: false,
    limit: '',
    active: true,
  });

  const [selectedFeatures, setSelectedFeatures] = useState<FeatureI[]>([]);
  const [newFeatureName, setNewFeatureName] = useState('');
  const [newFeatureIcon, setNewFeatureIcon] = useState<'Check' | 'Infinity'>('Check');
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; featureId: number } | null>(null);

  // Query para obtener el plan existente
  const { data: plan, isLoading: planLoading } = useQuery({
    queryKey: ['plan', planId],
    queryFn: () => getPlanById(Number(planId)),
    enabled: !!planId,
  });

  // Query para obtener features existentes
  const { data: availableFeatures = [], isLoading: featuresLoading } = useQuery({
    queryKey: ['features'],
    queryFn: getFeatures,
  });

  // Poblar formulario cuando se carga el plan
  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name,
        description: plan.description,
        priceEuro: (plan.price / 100).toFixed(2),
        icon: plan.icon || '',
        popular: plan.popular,
        limit: plan.limit ? String(plan.limit) : '',
        active: plan.active,
      });

      // Mapear planFeatures a selectedFeatures
      const planFeatures: FeatureI[] = plan.planFeatures.map((pf) => ({
        featureId: pf.feature.id,
        included: pf.included,
      }));

      setSelectedFeatures(planFeatures);
    }
  }, [plan, availableFeatures]);

  // Mutation para crear feature
  const createFeatureMutation = useMutation({
    mutationFn: (data: { icon: string; name: string }) =>
      createFeature(data.icon, data.name),
    onSuccess: (newFeature) => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
      setNewFeatureName('');
      setNewFeatureIcon('Check');
      toast.success(t('featureCreated'));
      setSelectedFeatures((prev) => [...prev, { featureId: newFeature.id, included: true }]);
    },
    onError: () => {
      toast.error(t('errorCreatingFeature'));
    },
  });

  // Mutation para eliminar feature
  const deleteFeatureMutation = useMutation({
    mutationFn: (featureId: number) => deleteFeature(featureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['features'] });
      toast.success(t('featureDeleted'));
      setDeleteConfirm(null);
    },
    onError: () => {
      toast.error(t('cannotDeleteFeatureInUse'));
    },
  });

  // Mutation para actualizar el plan
  const updatePlanMutation = useMutation({
    mutationFn: (payload: { planId: number; payload: CreatePlanPayload }) =>
      putPlan(payload),
    onSuccess: () => {
      toast.success(t('planUpdatedSuccessfully'));
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      navigate('/subscriptions-manage');
    },
    onError: () => {
      toast.error(t('errorUpdatingPlan'));
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleAddFeature = async () => {
    if (newFeatureName.trim() === '') {
      toast.error(t('pleaseEnterFeatureName'));
      return;
    }
    createFeatureMutation.mutate({ icon: newFeatureIcon, name: newFeatureName.trim() });
  };

  const handleSelectFeature = (featureId: number) => {
    setSelectedFeatures((prev) => [
      ...prev,
      { featureId, included: true }
    ]);
  };

  const handleRemoveFeature = (featureId: number) => {
    setSelectedFeatures((prev) => prev.filter((f) => f.featureId !== featureId));
  };

  const handleToggleIncluded = (featureId: number) => {
    setSelectedFeatures((prev) =>
      prev.map((f) =>
        f.featureId === featureId ? { ...f, included: !f.included } : f
      )
    );
  };

  const handleDeleteFeature = (featureId: number) => {
    deleteFeatureMutation.mutate(featureId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error(t('planNameRequired'));
      return;
    }
    if (!formData.description.trim()) {
      toast.error(t('descriptionRequired'));
      return;
    }
    if (!formData.priceEuro) {
      toast.error(t('priceRequired'));
      return;
    }

    const priceInCents = Math.round(parseFloat(formData.priceEuro) * 100);

    const planData: CreatePlanPayload = {
      name: formData.name,
      description: formData.description,
      price: priceInCents,
      icon: formData.icon || null,
      features: selectedFeatures,
      popular: formData.popular,
      limit: formData.limit ? parseInt(formData.limit) : null,
      active: formData.active,
    };

    updatePlanMutation.mutate({ planId: Number(planId), payload: planData });
  };

  if (planLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <p className="text-gray-600">{t('planNotFound')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/subscriptions-manage')}
          className="mb-6 inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('back')}
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('editPlan')}</h1>
          <p className="text-gray-600">{t('updatePlanDetails')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Información básica */}
          <div className="rounded-2xl bg-white p-8 shadow-sm">
            <h2 className="mb-6 text-lg font-semibold text-gray-900">{t('basicInformation')}</h2>

            <div className="grid gap-6 sm:grid-cols-2">
              {/* Nombre */}
              <div>
                <label htmlFor="name" className="mb-2 block text-sm font-medium text-gray-700">
                  {t('planName')} *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('planNamePlaceholder')}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Precio en euros */}
              <div>
                <label htmlFor="priceEuro" className="mb-2 block text-sm font-medium text-gray-700">
                  {t('monthlyPrice')} *
                </label>
                <div className="relative">
                  <input
                    id="priceEuro"
                    name="priceEuro"
                    type="number"
                    step="0.01"
                    value={formData.priceEuro}
                    onChange={handleInputChange}
                    placeholder={t('monthlyPricePlaceholder')}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 pr-8 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
                </div>
              </div>

              {/* Icono del plan */}
              <div>
                <label htmlFor="icon" className="mb-2 block text-sm font-medium text-gray-700">
                  {t('planIcon')}
                </label>
                <select
                  id="icon"
                  name="icon"
                  value={formData.icon}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">{t('selectIcon')}</option>
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                </select>
              </div>

              {/* Límite */}
              <div>
                <label htmlFor="limit" className="mb-2 block text-sm font-medium text-gray-700">
                  {t('usageLimit')}
                </label>
                <input
                  id="limit"
                  name="limit"
                  type="number"
                  value={formData.limit}
                  onChange={handleInputChange}
                  placeholder={t('usageLimitPlaceholder')}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Descripción */}
            <div className="mt-6">
              <label htmlFor="description" className="mb-2 block text-sm font-medium text-gray-700">
                {t('description')} *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={t('descriptionPlaceholder')}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Checkboxes */}
            <div className="mt-6 space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="popular"
                  checked={formData.popular}
                  onChange={handleInputChange}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">{t('markAsPopular')}</span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm font-medium text-gray-700">{t('planActive')}</span>
              </label>
            </div>
          </div>

          {/* Características */}
          <div className="rounded-2xl bg-white p-8 shadow-sm">
            <h2 className="mb-6 text-lg font-semibold text-gray-900">{t('planFeatures')}</h2>

            {/* Crear nueva característica */}
            <div className="mb-8 rounded-lg bg-blue-50 p-4 border border-blue-200">
              <h3 className="mb-4 text-sm font-semibold text-blue-900">{t('createNewFeature')}</h3>
              <div className="grid gap-3 sm:grid-cols-[1fr,200px,auto]">
                <input
                  type="text"
                  value={newFeatureName}
                  onChange={(e) => setNewFeatureName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddFeature();
                    }
                  }}
                  placeholder={t('newFeaturePlaceholder')}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <select
                  value={newFeatureIcon}
                  onChange={(e) => setNewFeatureIcon(e.target.value as 'Check' | 'Infinity')}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="Check">{t('check')}</option>
                  <option value="Infinity">{t('infinity')}</option>
                </select>
                <button
                  type="button"
                  onClick={handleAddFeature}
                  disabled={createFeatureMutation.isPending}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  {t('add')}
                </button>
              </div>
            </div>

            {/* Características disponibles */}
            <div className="mb-8">
              <h3 className="mb-4 text-sm font-semibold text-gray-900">{t('availableFeatures')}</h3>
              {featuresLoading ? (
                <p className="text-gray-500">{t('loadingFeatures')}</p>
              ) : availableFeatures.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                  <p className="text-sm text-gray-600">{t('noFeaturesAvailable')}</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {availableFeatures.map((feature) => {
                    const isSelected = selectedFeatures.some((sf) => sf.featureId === feature.id);
                    return (
                      <div
                        key={feature.id}
                        className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 transition hover:border-indigo-300"
                      >
                        <div className="flex items-center gap-3">
                          {feature.icon === 'Check' ? (
                            <Check className="h-5 w-5 text-green-600" />
                          ) : (
                            <InfinityIcon className="h-5 w-5 text-blue-600" />
                          )}
                          <span className="text-sm font-medium text-gray-900">{feature.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {!isSelected ? (
                            <button
                              type="button"
                              onClick={() => handleSelectFeature(feature.id)}
                              className="rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-200"
                            >
                              {t('add')}
                            </button>
                          ) : (
                            <span className="rounded-lg bg-green-100 px-3 py-1.5 text-sm font-semibold text-green-700">
                              {t('added')}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm({ isOpen: true, featureId: feature.id })}
                            className="rounded-lg p-2 text-red-600 transition hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Características seleccionadas */}
            <div>
              <h3 className="mb-4 text-sm font-semibold text-gray-900">{t('selectedFeatures')}</h3>
              {selectedFeatures.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                  <p className="text-sm text-gray-600">{t('noFeaturesSelected')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedFeatures.map((sf) => {
                    const feature = availableFeatures.find((f) => f.id === sf.featureId);
                    if (!feature) return null;
                    return (
                      <div
                        key={sf.featureId}
                        className="flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 p-4"
                      >
                        <div className="flex items-center gap-3">
                          {feature.icon === 'Check' ? (
                            <Check className="h-5 w-5 text-green-600" />
                          ) : (
                            <InfinityIcon className="h-5 w-5 text-blue-600" />
                          )}
                          <span className="text-sm font-medium text-gray-900">{feature.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggleIncluded(sf.featureId)}
                            className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                              sf.included
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {sf.included ? t('included') : t('notIncluded')}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(sf.featureId)}
                            className="rounded-lg p-2 text-red-600 transition hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/subscriptions-manage')}
              className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              disabled={updatePlanMutation.isPending}
              className="rounded-lg bg-indigo-600 px-6 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {updatePlanMutation.isPending ? t('updating') : t('updatePlan')}
            </button>
          </div>
        </form>
      </div>

      {/* Modal de confirmación para eliminar feature */}
      {deleteConfirm?.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white shadow-lg">
            <div className="px-6 py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">{t('deleteFeature')}</h3>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                {t('areYouSureDeleteFeature')}
              </p>
            </div>
            <div className="flex gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => deleteConfirm && handleDeleteFeature(deleteConfirm.featureId)}
                disabled={deleteFeatureMutation.isPending}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deleteFeatureMutation.isPending ? t('deleting') : t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
 
export default EditPlan;